import { Button } from "../ui/button";
import { useLang } from "../../lib/LanguageContext";
import { BRAND, IMAGES } from "../../lib/brand";
import { REGISTER_URL } from "../../lib/config";
import { trackRegisterClick } from "../../lib/analytics";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";

export default function Hero() {
  const { t } = useLang();

  return (
    <section className="relative overflow-hidden pt-8 sm:pt-16 pb-20 sm:pb-28" data-testid="hero-section">
      {/* Decorative blobs */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />
      <div className="absolute top-60 -left-32 w-[400px] h-[400px] rounded-full bg-brand-red/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left column */}
        <div className="lg:col-span-6 xl:col-span-5 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-subtle border border-brand-red/20 text-brand-red text-xs font-bold tracking-wider mb-6" data-testid="hero-eyebrow">
            <Sparkles className="w-3.5 h-3.5" />
            {t.hero.eyebrow}
          </div>

          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-[4.25rem] leading-[1.05] tracking-tight text-ink" data-testid="hero-title">
            {t.hero.title1}{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-brand-red">{t.hero.titleAccent}</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-brand-orange/30 -z-0" aria-hidden />
            </span>{" "}
            {t.hero.title2}
          </h1>

          <p className="mt-6 text-base sm:text-lg text-ink-muted leading-relaxed max-w-xl" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="hero-cta-group">
            <a href={REGISTER_URL} rel="noopener" className="w-full sm:w-auto" onClick={() => trackRegisterClick("hero")}>
              <Button
                data-testid="hero-register-btn"
                size="lg"
                className="rounded-full bg-brand-red hover:bg-[#A60D25] text-white font-semibold px-7 h-14 text-base group w-full sm:w-auto"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <a href="#features">
              <Button
                data-testid="hero-learn-btn"
                size="lg"
                variant="outline"
                className="rounded-full border-2 border-ink/10 hover:border-ink text-ink font-semibold px-7 h-14 text-base bg-transparent w-full sm:w-auto"
              >
                {t.hero.ctaSecondary}
              </Button>
            </a>
          </div>

          <div className="mt-8 flex items-center gap-3 text-sm text-ink-muted" data-testid="hero-trust">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`w-8 h-8 rounded-full border-2 border-white ${
                  i === 1 ? 'bg-brand-red' : i === 2 ? 'bg-brand-orange' : i === 3 ? 'bg-ink' : 'bg-[#8B5A2B]'
                } flex items-center justify-center text-white text-xs font-bold`}>
                  {['R','B','C','A'][i-1]}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="font-medium">{t.hero.trustChip}</span>
            </div>
          </div>
        </div>

        {/* Right column — visual */}
        <div className="lg:col-span-6 xl:col-span-7 relative animate-fade-in delay-200" data-testid="hero-visual">
          <div className="relative">
            {/* Floating mascot — top-left, above image */}
            <img
              src={BRAND.mascot}
              alt="AsiaCommerce mascot"
              className="hidden md:block absolute -top-10 -left-6 w-24 h-24 lg:w-28 lg:h-28 rounded-2xl shadow-xl animate-float z-10"
              data-testid="hero-mascot"
            />

            {/* Main trader card */}
            <div className="relative rounded-3xl overflow-hidden soft-shadow border border-soft bg-white">
              <img
                src={IMAGES.trader}
                alt="Reseller using AsiaCommerce on phone"
                className="w-full h-[360px] sm:h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex gap-3">
                <div className="flex-1 bg-white/95 backdrop-blur rounded-2xl px-4 py-3">
                  <div className="text-[10px] font-bold uppercase text-ink-muted tracking-wider">{t.hero.stat1}</div>
                  <div className="font-display text-xl font-bold text-ink">380+</div>
                </div>
                <div className="flex-1 bg-white/95 backdrop-blur rounded-2xl px-4 py-3">
                  <div className="text-[10px] font-bold uppercase text-ink-muted tracking-wider">{t.hero.stat2}</div>
                  <div className="font-display text-xl font-bold text-brand-red">+185%</div>
                </div>
                <div className="flex-1 bg-white/95 backdrop-blur rounded-2xl px-4 py-3">
                  <div className="text-[10px] font-bold uppercase text-ink-muted tracking-wider">{t.hero.stat3}</div>
                  <div className="font-display text-xl font-bold text-ink">12</div>
                </div>
              </div>
            </div>

            {/* Floating profit chip */}
            <div className="absolute -top-5 -right-3 sm:-right-8 bg-white rounded-2xl soft-shadow border border-soft px-4 py-3 animate-float" style={{animationDelay: '1s'}}>
              <div className="text-[10px] font-bold uppercase text-ink-muted tracking-wider">Profit</div>
              <div className="font-display text-2xl font-extrabold text-green-600">+Rp 12.4M</div>
              <div className="text-xs text-ink-muted">this month</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
